import React, { ComponentType, RefObject, useMemo } from 'react';

import { FieldProps } from 'formik';
import MaskedInput from 'react-text-mask';

import IconCheck from 'components/icons/check';
import VisuallyHidden from 'components/visually-hidden';
import useIdCounter from 'hooks/use-id-counter';
import { FormValidationState } from 'utils/form';

import { FloatingLabelInputWrapper, IconContainer, Label, Message, StyledInput } from './styled';
import { IFloatingLabelInputProps } from './types';
import useInputHandles from './use-input-handles';
import useTestIds from './use-test-ids';

const isMobile = /Mobile/.test(navigator.userAgent);

export const FloatingLabelInput = React.forwardRef(
  (
    {
      'data-testid': testId,
      onBlur,
      onFocus,
      label,
      value,
      validation,
      maxLength,
      enableDefaultAlert,
      message,
      onChange,
      className,
      autoComplete,
      pattern,
      hasFooter,
      footer,
      mask,
      labelId,
      readOnly = false,
      ...rest
    }: IFloatingLabelInputProps,
    ref: RefObject<HTMLInputElement>
  ) => {
    const inputId = useIdCounter('floating-label-input');

    const messageId = useIdCounter('message');

    const { handleBlur, handleChange, handleFocus } = useInputHandles({
      inputId,
      isMobile,
      onBlur,
      onChange,
      onFocus,
    });

    const { wrapperTestId, labelTestId, validationTestId, messageTestId } = useTestIds(testId);

    // Getting refs to work with MaskedInput isn't as simple as passing it as a prop,
    // so I'm punting on solving this until we really need it.
    if (mask && ref) {
      throw new Error(
        "<FloatingLabelInput /> doesn't currently support using both 'mask' and 'ref' props at the same time"
      );
    }

    const inputProps = useMemo(
      () => ({
        'aria-describedby': messageId,
        'aria-labelledby': labelId,
        autoComplete,
        'data-testid': testId,
        hasFooter: !!hasFooter,
        id: inputId,
        maxLength,
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        pattern,
        readOnly,
        disabled: readOnly,
        type: 'text',
        validation,
        value,
      }),
      [
        autoComplete,
        hasFooter,
        handleBlur,
        handleChange,
        handleFocus,
        inputId,
        messageId,
        labelId,
        pattern,
        readOnly,
        testId,
        validation,
        value,
        maxLength,
      ]
    );

    const conditionalProps = useMemo(
      () =>
        mask
          ? {
              render: (internalRef: (n: HTMLInputElement) => void, props: typeof inputProps) => (
                <StyledInput ref={internalRef} {...props} />
              ),
              mask,
            }
          : { ref },
      // eslint thinks inputProps is used here because of the type
      [mask, ref] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const Input: ComponentType<any> = useMemo(() => (mask ? MaskedInput : StyledInput), [mask]);
    const isInvalid = FormValidationState.INVALID === validation;

    return (
      <FloatingLabelInputWrapper className={className} data-testid={wrapperTestId}>
        <Input
          {...inputProps}
          {...conditionalProps}
          {...rest}
          data-private
          aria-invalid={isInvalid}
        />
        {hasFooter && footer}
        <Label
          id={labelId}
          htmlFor={rest.id || inputId}
          hasValue={typeof value === 'number' || !!value}
          data-testid={labelTestId}
        >
          {label}
        </Label>
        <IconContainer validation={validation} data-testid={validationTestId}>
          {validation === FormValidationState.VALID && (
            <IconCheck data-testid="input-validation-valid" />
          )}
        </IconContainer>
        {message && (
          <>
            <Message id={messageId} data-testid={messageTestId} validation={validation}>
              {message}
            </Message>
            {isInvalid && enableDefaultAlert && (
              <VisuallyHidden role="alert">{message}</VisuallyHidden>
            )}
          </>
        )}
      </FloatingLabelInputWrapper>
    );
  }
);

export const FormikFloatingLabelInput = ({
  field,
  form,
  ...props
}: FieldProps & IFloatingLabelInputProps) => {
  const { errors, touched } = form;

  const fieldTouched = touched?.[field.name];
  const error = errors?.[field.name];
  const validation = error ? FormValidationState.INVALID : FormValidationState.VALID;

  return (
    <>
      <FloatingLabelInput
        {...field}
        message={fieldTouched && error ? error : undefined}
        validation={fieldTouched && validation ? validation : undefined}
        {...props}
      />
      {error && <VisuallyHidden role="alert">{error}</VisuallyHidden>}
    </>
  );
};
